import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/design-system.css';

function Privacy() {
  return (
    <div className="ps-page">
      <nav className="ps-nav">
        <div className="ps-container">
          <div className="ps-nav__inner">
            <Link to="/" className="ps-nav__logo">
              Postrify <span className="ps-nav__logo-dot" />
            </Link>
            <div className="ps-nav__right">
              <Link to="/" className="ps-btn ps-btn--ghost ps-btn--sm">← Home</Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="ps-container" style={{ maxWidth: '820px', paddingTop: '40px', paddingBottom: '80px' }}>
        <div style={{ marginBottom: '36px' }}>
          <span style={{ display:'inline-block', fontSize:'12px', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', color:'#4f46e5', marginBottom:'12px' }}>Legal</span>
          <h1 style={{ fontFamily:'Inter, sans-serif', fontSize:'clamp(28px,4vw,40px)', fontWeight:800, letterSpacing:'-0.6px', marginBottom:'10px' }}>Privacy Policy</h1>
          <p style={{ fontSize:'14px', color:'#8b8aab' }}>Last updated: March 27, 2026</p>
        </div>

        <div className="ps-card" style={{ lineHeight:'1.8', fontSize:'15px', color:'#4b4a6b' }}>
          <LegalSection title="1. Age Requirement &amp; Eligibility">
            <p><strong style={{color:'#16152e'}}>Minimum Age:</strong> Postrify is only available to users who are 13 years of age or older. By using this service, you confirm you meet this requirement. Users under 18 should have parental consent.</p>
            <p><strong style={{color:'#16152e'}}>Geographic Compliance:</strong> If you are located in the EU, you have rights under GDPR. If in California, you have rights under CCPA/CPRA.</p>
          </LegalSection>

          <LegalSection title="2. Information We Collect">
            <p>When you use Postrify, we collect the following information:</p>
            <ul>
              <li><strong>Account Information:</strong> Email address, username, full name</li>
              <li><strong>Brand Information:</strong> Brand names, niches, tone preferences, logos</li>
              <li><strong>Generated Content:</strong> AI-generated posts, images, captions, and hashtags</li>
              <li><strong>Social Media Tokens:</strong> OAuth access tokens for Facebook and Instagram (encrypted), including refresh tokens for API access</li>
              <li><strong>Social Media Data:</strong> Facebook page names, IDs, Instagram account data (retrieved via Graph API)</li>
              <li><strong>Usage Data:</strong> Log data, IP addresses, browser information</li>
              <li><strong>Cookie &amp; Tracking Data:</strong> Browser cookies, local storage for session management (no third-party tracking)</li>
            </ul>
          </LegalSection>

          <LegalSection title="3. How We Use Your Information">
            <p>We use collected information to:</p>
            <ul>
              <li>Provide and maintain our service</li>
              <li>Generate AI-powered social media content</li>
              <li>Publish content to your connected social media accounts</li>
              <li>Improve and personalize your experience</li>
              <li>Communicate with you about service updates and changes to this policy</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </LegalSection>

          <LegalSection title="4. Third-Party Data Sharing">
            <p>We share your data with the following third parties <em>only as needed to provide the service</em>:</p>
            <ul>
              <li><strong>Google Gemini API:</strong> AI text generation (captions, post content)</li>
              <li><strong>Ghost/Infip API:</strong> AI image generation</li>
              <li><strong>Facebook/Instagram Graph API:</strong> Publishing posts to your selected pages/accounts</li>
              <li><strong>Cloudinary:</strong> Image storage and CDN delivery</li>
              <li><strong>Service Providers:</strong> Web hosting, database, and infrastructure providers</li>
            </ul>
            <p style={{ marginTop:'14px', padding:'14px 16px', background:'#d1fae5', borderRadius:'8px', color:'#065f46', fontWeight:600, fontSize:'14px' }}>
              ✓ <strong>We never sell, trade, rent, or share your personal data with third parties for marketing purposes.</strong>
            </p>
          </LegalSection>

          <LegalSection title="5. Facebook &amp; Instagram API Data Usage">
            <p><strong style={{color:'#16152e'}}>OAuth Authorization:</strong> When you connect your Facebook or Instagram account, you authorize Postrify to:</p>
            <ul>
              <li>Retrieve your list of Facebook Pages and Instagram accounts</li>
              <li>Retrieve page/account information (names, IDs, connected accounts)</li>
              <li>Publish posts, images, and captions to your selected pages/accounts only</li>
              <li>Store encrypted access tokens and refresh tokens for future use</li>
            </ul>
            <p style={{marginTop:'12px'}}><strong style={{color:'#16152e'}}>You can revoke this authorization at any time:</strong></p>
            <ul>
              <li>Via Postrify: Go to Brand Settings → Disconnect [Platform]</li>
              <li>Via Facebook: Settings → Apps and Websites → Remove Postrify</li>
              <li>Via Instagram: Settings → Apps and Websites → Remove access</li>
            </ul>
            <p style={{marginTop:'12px', fontSize:'14px', color:'#dc2626', background:'#fee2e2', padding:'10px 12px', borderRadius:'6px'}}>
              <strong>Important:</strong> We comply with Facebook Platform Policies. Your tokens are encrypted and never shared with other apps.
            </p>
          </LegalSection>

          <LegalSection title="6. Data Security">
            <p>We implement the following security measures to protect your data:</p>
            <ul>
              <li>Password hashing using industry-standard bcrypt</li>
              <li>JWT token-based authentication with expiration</li>
              <li>Encrypted storage of OAuth tokens (encrypted at rest)</li>
              <li>HTTPS encryption for all data transmission (TLS 1.2+)</li>
              <li>Regular security audits and updates</li>
              <li>Limited access to sensitive data by authorized personnel only</li>
            </ul>
          </LegalSection>

          <LegalSection title="7. Data Retention">
            <p>We retain your data only as long as necessary to provide the service:</p>
            <ul>
              <li><strong>Account data:</strong> While your account is active + 90 days after deletion</li>
              <li><strong>Generated posts/images:</strong> 7 days (auto-deleted; can be deleted manually anytime)</li>
              <li><strong>OAuth tokens:</strong> Deleted immediately upon account deletion or app disconnection</li>
              <li><strong>Usage logs:</strong> 90 days (then anonymized)</li>
              <li><strong>Legal/compliance records:</strong> As required by applicable law</li>
            </ul>
          </LegalSection>

          <LegalSection title="8. Your Rights">
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data (see <Link to="/data-deletion" style={{color:'#4f46e5',fontWeight:600}}>Data Deletion Policy</Link>)</li>
              <li>Export your data in a portable format</li>
              <li>Opt-out of marketing communications</li>
              <li><strong>EU Residents (GDPR):</strong> Right to object to processing, restrict processing, data portability, lodge complaints with your national data protection authority</li>
              <li><strong>California Residents (CCPA/CPRA):</strong> Right to know, delete, and opt-out of data sales (we do not sell data). Contact support.postrify@gmail.com with "CCPA Request" in subject line</li>
            </ul>
          </LegalSection>

          <LegalSection title="9. Policy Changes">
            <p>We may update this privacy policy from time to time. Material changes will be communicated via email or prominent notice on the website. Continued use of Postrify after changes constitutes acceptance of the updated policy.</p>
          </LegalSection>

          <LegalSection title="10. Contact Us" last>
            <p style={{ marginBottom:'14px' }}>For privacy concerns, requests, or complaints:</p>
            <a href="mailto:support.postrify@gmail.com"
              style={{ display:'inline-block', padding:'12px 20px', background:'#e0e7ff', borderRadius:'8px', color:'#3730a3', fontWeight:700, fontSize:'15px', textDecoration:'none' }}>
              support.postrify@gmail.com
            </a>
            <p style={{marginTop:'20px', fontSize:'14px', color:'#666'}}>
              <strong>Response Times:</strong> 30 days (standard) | 15 days (EU GDPR) | 45 days (California CCPA)
            </p>
          </LegalSection>
        </div>

        <div style={{ marginTop:'28px', fontSize:'13px', color:'#8b8aab', display:'flex', gap:'20px', flexWrap:'wrap' }}>
          <Link to="/terms" style={{ color:'#4f46e5', fontWeight:600 }}>Terms of Service</Link>
          <Link to="/data-deletion" style={{ color:'#4f46e5', fontWeight:600 }}>Data Deletion</Link>
        </div>
      </div>

      <footer className="ps-footer">
        <div className="ps-container">
          © 2026 Postrify · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
        </div>
      </footer>
    </div>
  );
}

function LegalSection({ title, children, last }) {
  return (
    <div style={{ marginBottom: last ? 0 : '36px', paddingBottom: last ? 0 : '36px', borderBottom: last ? 'none' : '1px solid #e4e4ef' }}>
      <h2 style={{ fontFamily:'Inter, sans-serif', fontSize:'18px', fontWeight:700, color:'#16152e', marginBottom:'14px' }}>{title}</h2>
      <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
        {children}
      </div>
    </div>
  );
}

export default Privacy;
